import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Select, Typography, Tooltip, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { getActiveDivisions, DivisionDto } from 'app/shared/api/getDivision';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export interface TransferInstruction {
  divisionId: number | null;
  divisionName?: string;
  instructions: string;
}

interface Props {
  value?: TransferInstruction[];
  onChange?: (list: TransferInstruction[]) => void;
  onRemovePersist?: (list: TransferInstruction[]) => void;
}

const TransferInstructions: React.FC<Props> = ({ value = [], onChange, onRemovePersist }) => {
  const [rows, setRows] = useState<TransferInstruction[]>(value);
  const [divisions, setDivisions] = useState<DivisionDto[]>([]);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    getActiveDivisions().then(setDivisions);
  }, []);

  useEffect(() => {
    if (!value) return;
    if (JSON.stringify(value) !== JSON.stringify(rows)) {
      setRows(value);
    }
  }, [value]);

  const usedDivisionIds = useMemo(
    () => rows.map(r => r.divisionId).filter(Boolean) as number[],
    [rows]
  );

  const handleAdd = () => {
    const assignedCount = usedDivisionIds.length;
    if (divisions.length > 0 && assignedCount >= divisions.length) {
      return message.warning('All divisions already have instructions.');
    }
    const newRows = [...rows, { divisionId: null, instructions: '' }];
    setRows(newRows);
    setTouched(true);
    onChange?.(newRows);
  };

  const handleRemove = (index: number) => {
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
    setTouched(true);
    onChange?.(newRows);
    onRemovePersist?.(newRows);
  };

  const handleChange = (index: number, field: keyof TransferInstruction, val: any) => {
    const newRows = [...rows];
    (newRows[index] as any)[field] = val;
    if (field === 'divisionId') {
      const div = divisions.find(d => d.id === val);
      (newRows[index] as any).divisionName = div?.name ?? '';
    }
    setRows(newRows);
    setTouched(true);
    onChange?.(newRows);
  };

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (touched) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [touched]);

  return (
    <div style={{ marginTop: 24 }}>
      <Title level={5}>Arrival Transfer Instructions</Title>

      {rows.map((row, idx) => (
        <div
          key={idx}
          style={{
            border: '1px solid #e5e5e5',
            borderRadius: 6,
            padding: 16,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              width: '100%',
            }}
          >
            <div style={{ flex: 1 }}>
              <Form.Item label="Division" required style={{ marginBottom: 16 }}>
                <Select
                  placeholder="Select Division"
                  value={row.divisionId ?? undefined}
                  onChange={(val) => handleChange(idx, 'divisionId', val)}
                  style={{ width: '100%' }}
                  showSearch={{
                    optionFilterProp: 'children',
                    filterOption: (input, option) => {
                      const label = String(option?.children ?? '').toLowerCase();
                      const idText = String((option as any)?.value ?? '');
                      const q = input.toLowerCase();
                      return label.includes(q) || idText.includes(input);
                    },
                    filterSort: (a, b) =>
                      String(a?.children ?? '').localeCompare(String(b?.children ?? '')),
                  }}
                  allowClear
                >
                  {divisions.map((div) => (
                    <Option
                      key={div.id}
                      value={div.id}
                      disabled={usedDivisionIds.includes(div.id) && div.id !== row.divisionId}
                    >
                      {div.name}{!div.isActive ? ' (Inactive)' : ''}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Instructions"
                required
                style={{ width: '100%', marginBottom: 0 }}
              >
                <TextArea
                  rows={6}
                  maxLength={4000}
                  value={row.instructions}
                  onChange={(e) => handleChange(idx, 'instructions', e.target.value)}
                  placeholder="Enter transfer instructions..."
                  style={{ width: '100%', minHeight: 120, maxHeight: 240, overflowY: 'auto' }}
                />
              </Form.Item>
            </div>

            <Tooltip title="Remove">
              <Button
                type="text"
                shape="circle"
                size="large"
                icon={<DeleteOutlined />}
                aria-label="Remove transfer instruction"
                onClick={() => handleRemove(idx)}
              />
            </Tooltip>
          </div>
        </div>
      ))}

      <Button type="dashed" icon={<PlusOutlined />} onClick={handleAdd}>
        Add Arrival Transfer Instructions
      </Button>
    </div>
  );
};

export default TransferInstructions;